import React, { useMemo, useState } from 'react'
import type { CategoryItem } from '../../../../../gateways/breadstack/categoryApis'
import { useDebounce } from '../../../../../hooks/useDebounce'
import { useCategorySearchResults } from '../../../hooks/useCategorySearchResults'
import {
  buildCategoryLookups,
  buildParentMap,
  hasAncestorIn,
} from '../../../utils/categoryLookups'
import { CategoryAvatar } from '../../common'

interface Props {
  value: number[]
  onChange: (ids: number[]) => void
  categories: CategoryItem[]
  categoriesLoading: boolean
  disabled?: boolean
}

// The picker lists every remaining category (scrollable) rather than the
// 8-row cap used by the search-only Exclude Categories dialog.
const PICKER_LIMIT = 500

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: '#5A6B7B',
}

const warningBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  fontSize: 11,
  fontWeight: 600,
  color: '#B45309',
  background: '#FEF3C7',
  borderRadius: 999,
}

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '5px 8px 5px 6px',
  fontSize: 13,
  color: '#2C3E50',
  background: '#F8FAFC',
  border: '1px solid #E8ECF0',
  borderRadius: 8,
}

const dashedBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  color: '#5A6B7B',
  background: '#fff',
  border: '1px dashed #CBD5E1',
  borderRadius: 8,
  fontFamily: 'inherit',
}

const panelStyle: React.CSSProperties = {
  marginTop: 6,
  border: '1px solid #E8ECF0',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
  overflow: 'hidden',
}

const mutedRowStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 12,
  color: '#7F8C8D',
  textAlign: 'center',
}

export const ZoneExcludedCategoriesField: React.FC<Props> = ({
  value,
  onChange,
  categories,
  categoriesLoading,
  disabled = false,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 150)

  const { nameById, pathById, countById, imageById } = useMemo(
    () => buildCategoryLookups(categories),
    [categories],
  )
  const parentMap = useMemo(() => buildParentMap(categories), [categories])
  const hiddenIds = useMemo(() => new Set(value), [value])
  // Excluding a parent also blocks its sub-categories, so the picker hides
  // descendants of anything already excluded.
  const results = useCategorySearchResults({
    categories,
    query: debouncedQuery,
    hiddenIds,
    coveredIds: hiddenIds,
    limit: PICKER_LIMIT,
  })
  const allExcluded =
    !categoriesLoading &&
    categories.length > 0 &&
    categories.every((c) => {
      const id = Number(c.id)
      return hiddenIds.has(id) || hasAncestorIn(id, hiddenIds, parentMap)
    })

  const togglePicker = () => {
    setPickerOpen((prev) => !prev)
    setQuery('')
  }

  const handleAdd = (cat: CategoryItem) => {
    const id = Number(cat.id)
    if (!hiddenIds.has(id)) {
      // Drop sub-categories already listed — the new parent covers them.
      const added = new Set([id])
      const kept = value.filter((x) => !hasAncestorIn(x, added, parentMap))
      onChange([...kept, id])
    }
    setPickerOpen(false)
    setQuery('')
  }

  const handleRemove = (id: number) => onChange(value.filter((x) => x !== id))

  const renderPickerBody = () => {
    if (categoriesLoading) return <div style={mutedRowStyle}>Loading categories…</div>
    if (allExcluded) {
      return <div style={mutedRowStyle}>All categories have been excluded</div>
    }
    if (results.length === 0) {
      return <div style={mutedRowStyle}>No categories match "{query}"</div>
    }
    return results.map(({ cat, isChild }) => (
      <button
        key={cat.id}
        type="button"
        onClick={() => handleAdd(cat)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          textAlign: 'left',
          padding: '8px 12px',
          paddingLeft: isChild ? 32 : 12,
          fontSize: 13,
          fontWeight: isChild ? 400 : 500,
          color: isChild ? '#5A6B7B' : '#2C3E50',
          background: 'transparent',
          border: 'none',
          borderBottom: '1px solid #F1F5F9',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#F8FAFC'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent'
        }}
      >
        <CategoryAvatar name={cat.name} image={imageById.get(Number(cat.id))} size={24} />
        <span>{cat.name}</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94A3B8' }}>
          {cat.productCount ?? 0} products
        </span>
      </button>
    ))
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <span style={labelStyle}>Excluded Categories</span>
        {value.length > 0 && (
          <span style={warningBadgeStyle}>{value.length} excluded</span>
        )}
      </div>
      <p style={{ fontSize: 12, color: '#7F8C8D', margin: '0 0 10px' }}>
        Products in these categories (and their sub-categories) won't be
        available for mail order in this zone.
      </p>

      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {value.map((id) => {
            const name = nameById.get(id) ?? `#${id}`
            return (
              <span key={id} style={chipStyle} title={pathById.get(id) ?? name}>
                <CategoryAvatar name={name} image={imageById.get(id)} size={20} />
                <span>
                  {categoriesLoading && !nameById.has(id) ? '…' : name}
                </span>
                {countById.has(id) && (
                  <span style={{ fontSize: 11, color: '#94A3B8' }}>
                    {countById.get(id)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(id)}
                  disabled={disabled}
                  aria-label={`Remove ${name}`}
                  style={{
                    padding: 2,
                    border: 'none',
                    background: 'transparent',
                    color: '#94A3B8',
                    fontSize: 14,
                    lineHeight: 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  ×
                </button>
              </span>
            )
          })}
        </div>
      )}

      <button
        type="button"
        onClick={togglePicker}
        disabled={disabled}
        aria-expanded={pickerOpen}
        style={{ ...dashedBtnStyle, cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <span style={{ fontSize: 15, lineHeight: 1 }}>+</span>
        Add Category Exclusion
      </button>

      {pickerOpen && (
        <div style={panelStyle}>
          <div style={{ padding: 8, borderBottom: '1px solid #F1F5F9' }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search categories…"
              aria-label="Search categories"
              autoComplete="off"
              // biome-ignore lint/a11y/noAutofocus: picker opens on explicit click; focus the search immediately
              autoFocus
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '6px 10px',
                fontSize: 13,
                color: '#2C3E50',
                border: '1px solid #E8ECF0',
                borderRadius: 6,
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div style={{ maxHeight: 192, overflowY: 'auto' }}>{renderPickerBody()}</div>
        </div>
      )}
    </div>
  )
}
